import { useEffect, useRef, useState } from "react";
import { Star, Camera, Loader2, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Overline } from "../components/Reveal";
import { API, BOOKING_TYPES } from "../data";

const Stars = ({ n, size = "h-4 w-4" }) => (
  <div className="flex gap-1">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`${size} ${i < n ? "text-gold fill-gold" : "text-slate-600"}`} />
    ))}
  </div>
);

const EMPTY = { name: "", location: "", service: "Holiday Lighting", rating: 5, text: "" };

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    axios.get(`${API}/reviews`).then((r) => setReviews(r.data.reviews || [])).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      toast.error("Please add your name and a few words about your experience.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("location", form.location);
      fd.append("service", form.service);
      fd.append("rating", String(form.rating));
      fd.append("text", form.text);
      if (photo) fd.append("photo", photo);
      const res = await axios.post(`${API}/reviews`, fd);
      setReviews((prev) => [res.data, ...prev]);
      setForm(EMPTY);
      setPhoto(null);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Thank you! Your review is now live on this page.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not publish your review. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const avg = reviews.length ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1) : "5.0";

  return (
    <div data-testid="reviews-page" className="pt-20">
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-16 pb-14">
        <Reveal>
          <Overline>Reviews</Overline>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-slate-100 tracking-tight leading-[1.1]">
            The glow, <span className="text-gold-gradient">reviewed</span>
          </h1>
          <div className="mt-8 flex flex-wrap items-center gap-6">
            <div data-testid="reviews-average" className="flex items-center gap-3">
              <span className="font-serif text-5xl font-bold text-gold-gradient">{avg}</span>
              <div>
                <Stars n={Math.round(Number(avg))} />
                <p className="text-xs text-slate-500 mt-1">{reviews.length} verified reviews</p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* WRITE A REVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-16">
        <Reveal>
          <form
            onSubmit={submit}
            data-testid="review-form"
            className="rounded-3xl border border-gold/25 bg-card2/50 p-6 sm:p-10"
          >
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-slate-100 mb-2">Share your glow</h2>
            <p className="text-sm text-slate-400 mb-8">Had work done by GlowMode? Tell the neighborhood — your review publishes instantly.</p>

            <div className="grid gap-5 sm:grid-cols-2 mb-5">
              <div>
                <label htmlFor="review-name" className="block text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2">Your name *</label>
                <input
                  id="review-name"
                  data-testid="review-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jessica M."
                  className="w-full rounded-xl bg-obsidian/70 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-gold/60 focus:ring-1 focus:ring-gold/40 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="review-location" className="block text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2">City</label>
                <input
                  id="review-location"
                  data-testid="review-location-input"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Draper, UT"
                  className="w-full rounded-xl bg-obsidian/70 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-gold/60 focus:ring-1 focus:ring-gold/40 transition-colors"
                />
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2 mb-5">
              <div>
                <label className="block text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2">Service we did</label>
                <div data-testid="review-service-picker" className="flex flex-wrap gap-2">
                  {BOOKING_TYPES.slice(0, 7).map((t) => (
                    <button
                      type="button"
                      key={t}
                      data-testid={`review-service-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => setForm({ ...form, service: t })}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                        form.service === t ? "border-gold text-gold-light bg-gold/10" : "border-white/10 text-slate-400 hover:border-gold/40"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2">Your rating</label>
                <div className="flex gap-2 pt-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      type="button"
                      key={n}
                      data-testid={`review-star-${n}`}
                      onClick={() => setForm({ ...form, rating: n })}
                      aria-label={`${n} stars`}
                      className="transition-transform duration-200 hover:scale-125"
                    >
                      <Star className={`h-7 w-7 ${n <= form.rating ? "text-gold fill-gold" : "text-slate-600"}`} />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-5">
              <label htmlFor="review-text" className="block text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2">Your review *</label>
              <textarea
                id="review-text"
                data-testid="review-text-input"
                rows={4}
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="How did we do? What did you love about the final look?"
                className="w-full rounded-xl bg-obsidian/70 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-gold/60 focus:ring-1 focus:ring-gold/40 transition-colors resize-none"
              />
            </div>

            <div className="flex flex-wrap items-center gap-5">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickPhoto} data-testid="review-photo-input" />
              <button
                type="button"
                data-testid="review-photo-button"
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-dashed border-silver/40 text-sm text-slate-300 hover:border-gold/60 hover:text-gold-light transition-all duration-300"
              >
                <Camera className="h-4 w-4" />
                {photo ? "Change photo" : "Add a photo of your lights"}
              </button>
              {preview && (
                <img src={preview} alt="Review upload preview" data-testid="review-photo-preview" className="h-14 w-14 rounded-lg object-cover ring-1 ring-gold/40" />
              )}
              <button
                type="submit"
                data-testid="review-submit-button"
                disabled={sending}
                className="ml-auto inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-obsidian bg-gradient-to-br from-gold-light via-gold to-gold-dark transition-all duration-300 hover:shadow-[0_0_30px_rgba(233,199,103,0.45)] hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? "Publishing..." : "Publish Review"}
              </button>
            </div>
          </form>
        </Reveal>
      </section>

      {/* REVIEW WALL */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24">
        {loading ? (
          <div className="flex justify-center py-20" data-testid="reviews-loading">
            <Loader2 className="h-8 w-8 text-gold animate-spin" />
          </div>
        ) : (
          <div data-testid="reviews-wall" className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {reviews.map((r, i) => (
              <Reveal key={r.id} delay={(i % 3) * 0.06}>
                <article data-testid={`review-card-${r.id}`} className="h-full rounded-2xl border border-white/5 bg-card2/60 overflow-hidden transition-all duration-300 hover:border-gold/40 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
                  {(r.photo_url || r.photo_path) && (
                    <div className="h-52 overflow-hidden">
                      <img
                        src={r.photo_url || `${API}/review-photo/${r.photo_path}`}
                        alt={`${r.service || "Lighting"} by ${r.name}`}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <Stars n={r.rating} />
                      {r.service && <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-gold/80">{r.service}</span>}
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed mb-5">"{r.text}"</p>
                    <p className="text-sm font-semibold text-slate-100">{r.name}</p>
                    {r.location && <p className="text-xs text-slate-500">{r.location}</p>}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
